import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { IconArrowRight, IconX } from '@tabler/icons-react';
import styles from './KpiSidePanel.module.scss';
import { useProjectIntelligence } from '../../hooks/useProjectIntelligence';
import { KpiScope } from '../../api/model';
import {
  HEALTH_DIMENSIONS,
  KPI,
  KpiIndex,
  categorical,
  healthKeyFromStatus,
  indexProjectKpis,
  numeric,
  numericByScopeId,
} from '../ReportMetrics/intelligence';
import {
  ACTION_STATE_TONE,
  HistoryIndex,
  TONE_COLOR,
  Tone,
  actionState,
  actionSummary,
  byUrgency,
  changeOver,
  checkpointRows,
  conspicuousDependencies,
  conspicuousDeliverables,
  deliverableRows,
  dependencyRows,
  directionOf,
  indexHistory,
  inverseRateTone,
  isoDay,
  actionTitle,
  mainDrivers,
  objectSearch,
  ObjectTarget,
  readinessPoints,
  scoreBand,
  signed,
  withinDays,
} from '../ReportMetrics/detail';
import type { ReportTab } from '../ReportMetrics/ReportMetrics';
import ScoreRing from '../ReportMetrics/widgets/ScoreRing';
import KpiHistoryChart from '../ReportMetrics/widgets/KpiHistoryChart';
import StatusBadge from '../ReportMetrics/widgets/StatusBadge';
import InlineBar from '../ReportMetrics/widgets/InlineBar';

export type PanelKind = 'health' | 'readiness' | 'execution' | 'quality' | 'dependency' | 'progress' | 'actions';

interface KpiSidePanelProps {
  projectId?: number;
  kind: PanelKind;
  onClose: () => void;
}

/** The side panel's "Verlauf (letzte 12 Wochen)". */
const HISTORY_DAYS = 84;

interface Diagnosis {
  label: string;
  value?: number;
  /** Rates where lower is better are coloured by their complement. */
  lowerIsBetter?: boolean;
}

interface Finding {
  key: string;
  text: string;
  detail?: string;
  tone: Tone;
  /** The object it is about - findings are clickable where they have one (spec §6). */
  target?: ObjectTarget;
}

interface PanelContent {
  title: string;
  value?: number;
  /** Shown instead of a percentage ring, e.g. a count of actions. */
  count?: number;
  status?: { tone: Tone; label: string };
  trend?: number;
  indicator?: { label: string; value: string; critical: boolean };
  confidence?: number;
  diagnosisTitle: string;
  diagnosis: Diagnosis[];
  historyKey?: string;
  findingsTitle: string;
  findings: Finding[];
  tab: ReportTab;
}

const kpiLabel = (t: TFunction, key: string) => t(`report.detail.kpiName.${key.replace(/\./g, '_')}`);

function scoreStatus(t: TFunction, value?: number) {
  const band = scoreBand(value);
  return band ? { tone: band.tone, label: t(`report.health.status.${band.key}`) } : undefined;
}

/**
 * KPI side panel (Cockpit spec): the step between a Cockpit tile and the full
 * Project Intelligence page. One layout for all seven KPIs - value and status,
 * the 4-6 KPIs that explain it, twelve weeks of history, up to five concrete
 * findings, and the way on to the detailed analysis. Nothing in it is a new
 * score; every number is one the detail page shows too.
 */
const KpiSidePanel: React.FC<KpiSidePanelProps> = ({ projectId, kind, onClose }) => {
  const { t } = useTranslation(['templates', 'gantt']);
  const navigate = useNavigate();
  const data = useProjectIntelligence(projectId, { milestones: false });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const index = useMemo(() => indexProjectKpis(data.kpis), [data.kpis]);
  const historyIndex = useMemo(() => indexHistory(data.history), [data.history]);

  const phaseNames = useMemo(() => {
    const names = new Map<number, string>();
    const ordered = [...(data.phases ?? [])].sort((a, b) =>
      (a?.startTime ?? '9999').localeCompare(b?.startTime ?? '9999'),
    );
    for (const phase of ordered) {
      if (phase?.id == null) continue;
      const fallback = phase.name || `#${phase.id}`;
      names.set(phase.id, phase.customName || (phase.type ? t(`gantt:phaseTypes.${phase.type}`, fallback) : fallback));
    }
    return names;
  }, [data.phases, t]);

  const content = useMemo(
    () => buildContent(kind, t, index, historyIndex, data, phaseNames),
    [kind, t, index, historyIndex, data, phaseNames],
  );

  const openDetails = () => {
    if (!projectId) return;
    void navigate({
      to: '/project/$projectId',
      params: { projectId: String(projectId) },
      search: (previous) => ({ ...previous, section: 'report-metrics', report: content.tab }),
    });
    onClose();
  };

  const openFinding = (target: ObjectTarget) => {
    if (!projectId) return;
    void navigate({
      to: '/project/$projectId',
      params: { projectId: String(projectId) },
      search: (previous) => ({ ...previous, ...objectSearch(target) }),
      replace: true,
    });
  };

  const history = content.historyKey ? withinDays(historyIndex.get(content.historyKey), HISTORY_DAYS) : [];
  const trend = content.trend;
  const trendDirection = directionOf(trend);

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <aside className={styles.panel} role="dialog" aria-label={content.title}>
        <header className={styles.header}>
          <div>
            <span className={styles.eyebrow}>{t('report.title')}</span>
            <h3 className={styles.heading}>{t('report.panel.heading')}</h3>
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label={t('report.panel.close')}>
            <IconX size={18} />
          </button>
        </header>

        <section className={styles.section}>
          <h4 className={styles.kpiTitle}>{content.title}</h4>
          <div className={styles.summary}>
            <div className={styles.summaryMain}>
              {content.count != null ? (
                <span className={styles.bigCount}>{content.count}</span>
              ) : (
                <ScoreRing
                  value={content.value}
                  color={TONE_COLOR[content.status?.tone ?? 'neutral']}
                  size={88}
                  thickness={8}
                />
              )}
              {content.status && <StatusBadge tone={content.status.tone} label={content.status.label} />}
            </div>
            <div className={styles.summarySide}>
              <span className={styles.sideLabel}>{t('report.cockpit.trend')}</span>
              <span
                className={
                  trendDirection === 'up' ? styles.good : trendDirection === 'down' ? styles.bad : styles.flat
                }
              >
                {typeof trend === 'number' ? signed(trend) : '—'}
              </span>
              {content.indicator && (
                <>
                  <span className={styles.sideLabel}>{content.indicator.label}</span>
                  <span className={content.indicator.critical ? styles.bad : styles.value}>
                    {content.indicator.value}
                  </span>
                </>
              )}
              {content.confidence != null && (
                <>
                  <span className={styles.sideLabel}>{t('report.confidence.title')}</span>
                  <span className={styles.value}>{Math.round(content.confidence)}%</span>
                </>
              )}
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h5 className={styles.sectionTitle}>{content.diagnosisTitle}</h5>
          <div className={styles.diagnosis}>
            {content.diagnosis.map((row) => (
              <div key={row.label} className={styles.diagnosisRow}>
                <span className={styles.diagnosisLabel}>{row.label}</span>
                <InlineBar
                  value={row.value}
                  tone={row.lowerIsBetter ? inverseRateTone(row.value) : scoreBand(row.value)?.tone}
                />
              </div>
            ))}
          </div>
        </section>

        {content.historyKey && (
          <section className={styles.section}>
            <h5 className={styles.sectionTitle}>{t('report.panel.history')}</h5>
            <div className={styles.chart}>
              {history.length > 0 ? (
                <KpiHistoryChart data={history} seriesName={content.title} max={100} />
              ) : (
                <p className={styles.empty}>{t('report.trendScore.noHistory')}</p>
              )}
            </div>
          </section>
        )}

        <section className={styles.section}>
          <h5 className={styles.sectionTitle}>{content.findingsTitle}</h5>
          {content.findings.length === 0 ? (
            <p className={styles.empty}>{t('report.panel.noFindings')}</p>
          ) : (
            <ul className={styles.findings}>
              {content.findings.map((finding) => (
                <li
                  key={finding.key}
                  className={`${styles.finding} ${finding.target ? styles.findingLink : ''}`}
                  onClick={finding.target ? () => openFinding(finding.target as ObjectTarget) : undefined}
                >
                  <span className={styles.dot} style={{ background: TONE_COLOR[finding.tone] }} />
                  <span className={styles.findingText}>{finding.text}</span>
                  {finding.detail && (
                    <span className={finding.tone === 'critical' ? styles.bad : styles.findingDetail}>
                      {finding.detail}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <footer className={styles.footer}>
          <button type="button" className={styles.cta} onClick={openDetails}>
            {t('report.panel.details')}
            <IconArrowRight size={16} stroke={2} />
          </button>
        </footer>
      </aside>
    </>
  );
};

type IntelligenceData = ReturnType<typeof useProjectIntelligence>;

function buildContent(
  kind: PanelKind,
  t: TFunction,
  index: KpiIndex,
  history: HistoryIndex,
  data: IntelligenceData,
  phaseNames: Map<number, string>,
): PanelContent {
  const weekTrend = (key: string) => changeOver(history.get(key), numeric(index, key), 7)?.delta;
  const deliverables = deliverableRows(data.findings?.deliverables, data.kpis, phaseNames);
  const checkpoints = checkpointRows(data.findings?.openCheckpoints, data.findings?.deliverables, phaseNames);
  const dependencies = dependencyRows(data.dependencyGraph);
  const days = (count: number) => t('report.detail.daysLate', { count });

  switch (kind) {
    case 'health': {
      const value = numeric(index, KPI.projectHealthScore);
      const key = healthKeyFromStatus(categorical(index, KPI.projectHealthStatus));
      return {
        title: t('report.health.title'),
        value,
        status: key ? { tone: scoreBand(value)?.tone ?? 'neutral', label: t(`report.health.status.${key}`) } : scoreStatus(t, value),
        trend: numeric(index, KPI.projectHealthTrend),
        indicator: {
          label: t('report.detail.overview.openActions'),
          value: `${actionSummary(data.actionPoints).open}`,
          critical: actionSummary(data.actionPoints).overdue > 0,
        },
        confidence: numeric(index, KPI.healthConfidence),
        diagnosisTitle: t('report.panel.components'),
        diagnosis: [
          ...HEALTH_DIMENSIONS.map((d) => ({ label: t(`report.dimensions.${d.labelKey}`), value: numeric(index, d.key) })),
          { label: t('report.kpi.projectProgress.label'), value: numeric(index, KPI.projectProgress) },
        ],
        historyKey: KPI.projectHealthScore,
        findingsTitle: t('report.panel.drivers'),
        findings: mainDrivers(deliverables, checkpoints, dependencies, index, 3).map((d) => ({
          key: d.id,
          text: t(`report.detail.driver.${d.kind}.theme`, d.params),
          detail: t(`report.detail.driver.${d.kind}.impact`, d.params),
          tone: d.tone,
          target: d.target,
        })),
        tab: 'overview',
      };
    }
    case 'readiness': {
      const value = numeric(index, KPI.readinessScore);
      const points = readinessPoints(data.findings?.deliverables);
      return {
        title: t('report.dimensions.readiness'),
        value,
        status: scoreStatus(t, value),
        trend: weekTrend(KPI.readinessScore),
        indicator: { label: t('report.panel.openTopics'), value: `${points.length}`, critical: points.length > 0 },
        confidence: numeric(index, KPI.readinessConfidence),
        diagnosisTitle: t('report.panel.readinessKpis'),
        diagnosis: [
          KPI.planningReadinessLo,
          KPI.projectLoDataReadiness,
          KPI.scheduleConsistency,
          KPI.ownershipReadiness,
          KPI.structureReadiness,
          KPI.qualityReadiness,
        ].map((key) => ({ label: kpiLabel(t, key), value: numeric(index, key) })),
        historyKey: KPI.readinessScore,
        findingsTitle: t('report.detail.readiness.openPoints'),
        findings: points.slice(0, 4).map((p) => ({
          key: p.gap,
          text: t(`report.detail.gap.${p.gap}`),
          detail: `${p.count}`,
          tone: p.highCount > 0 ? 'critical' : 'warning',
        })),
        tab: 'readiness',
      };
    }
    case 'execution': {
      const value = numeric(index, KPI.executionScore);
      const overdue = numeric(index, KPI.overdueDeliverableCount) ?? 0;
      return {
        title: t('report.dimensions.execution'),
        value,
        status: scoreStatus(t, value),
        trend: weekTrend(KPI.executionScore),
        indicator: { label: t('report.cockpit.overdueDeliverables'), value: `${Math.round(overdue)}`, critical: overdue > 0 },
        confidence: numeric(index, KPI.executionConfidence),
        diagnosisTitle: t('report.panel.executionKpis'),
        diagnosis: [
          { label: kpiLabel(t, KPI.projectProgressPerformance), value: numeric(index, KPI.projectProgressPerformance) },
          { label: kpiLabel(t, KPI.overdueDeliverableRate), value: numeric(index, KPI.overdueDeliverableRate), lowerIsBetter: true },
          { label: kpiLabel(t, KPI.overduePhaseRate), value: numeric(index, KPI.overduePhaseRate), lowerIsBetter: true },
          { label: kpiLabel(t, KPI.scheduleReliability), value: numeric(index, KPI.scheduleReliability) },
          { label: kpiLabel(t, KPI.criticalDeliveryPerformance), value: numeric(index, KPI.criticalDeliveryPerformance) },
        ],
        historyKey: KPI.executionScore,
        findingsTitle: t('report.panel.drivers'),
        findings: conspicuousDeliverables(deliverables).slice(0, 3).map((d) => ({
          key: `lo-${d.id}`,
          text: d.name ?? `#${d.id}`,
          detail: d.daysOverdue != null ? days(d.daysOverdue) : `${Math.round(d.variance ?? 0)} PP`,
          tone: d.criticality === 'HIGH' || d.daysOverdue != null ? 'critical' : 'warning',
          target: { kind: 'deliverable', id: d.id as number },
        })),
        tab: 'execution',
      };
    }
    case 'quality': {
      const value = numeric(index, KPI.qualityScore);
      const delayed = checkpoints.filter((cp) => cp.delayed);
      return {
        title: t('report.dimensions.quality'),
        value,
        status: scoreStatus(t, value),
        trend: weekTrend(KPI.qualityScore),
        indicator: { label: t('report.detail.overview.delayedCheckpoints'), value: `${delayed.length}`, critical: delayed.length > 0 },
        confidence: numeric(index, KPI.qualityConfidence),
        diagnosisTitle: t('report.panel.qualityKpis'),
        diagnosis: [
          { label: kpiLabel(t, KPI.qaCoverage), value: numeric(index, KPI.qaCoverage) },
          { label: kpiLabel(t, KPI.projectQaCompletion), value: numeric(index, KPI.projectQaCompletion) },
          { label: kpiLabel(t, KPI.qaTimeliness), value: numeric(index, KPI.qaTimeliness) },
          { label: kpiLabel(t, KPI.delayedCheckpointRate), value: numeric(index, KPI.delayedCheckpointRate), lowerIsBetter: true },
          { label: t('report.detail.quality.assured'), value: numeric(index, KPI.qualityAssuredProjectProgress) },
        ],
        historyKey: KPI.qualityScore,
        findingsTitle: t('report.panel.openQa'),
        findings: checkpoints.slice(0, 3).map((cp) => ({
          key: `cp-${cp.id}`,
          text: `${cp.deliverableName}: ${(cp.label ?? cp.taskName ?? '').slice(0, 48)}${(cp.label?.length ?? 0) > 48 ? '…' : ''}`,
          detail: cp.daysOverdue != null ? days(cp.daysOverdue) : undefined,
          tone: cp.delayed ? 'critical' : 'neutral',
          target: { kind: 'checkpoint', id: cp.id as number },
        })),
        tab: 'quality',
      };
    }
    case 'dependency': {
      const value = numeric(index, KPI.dependencyScore);
      const conspicuous = conspicuousDependencies(dependencies);
      const violated = dependencies.filter((d) => d.state === 'VIOLATED').length;
      return {
        title: t('report.dimensions.dependency'),
        value,
        status: scoreStatus(t, value),
        trend: weekTrend(KPI.dependencyScore),
        indicator: { label: t('report.detail.overview.hardViolations'), value: `${violated}`, critical: violated > 0 },
        confidence: numeric(index, KPI.dependencyConfidence),
        diagnosisTitle: t('report.panel.dependencyKpis'),
        diagnosis: [
          { label: kpiLabel(t, KPI.hardDependencyViolationRate), value: numeric(index, KPI.hardDependencyViolationRate), lowerIsBetter: true },
          { label: kpiLabel(t, KPI.parallelDependencyRiskRate), value: numeric(index, KPI.parallelDependencyRiskRate), lowerIsBetter: true },
          { label: kpiLabel(t, KPI.overduePredecessorRate), value: numeric(index, KPI.overduePredecessorRate), lowerIsBetter: true },
          { label: kpiLabel(t, KPI.dependencyIntegrity), value: numeric(index, KPI.dependencyIntegrity) },
        ],
        historyKey: KPI.dependencyScore,
        findingsTitle: t('report.panel.criticalDependencies'),
        findings: conspicuous.slice(0, 3).map((d) => ({
          key: `dep-${d.id}`,
          text: `${d.predecessor} → ${d.successor}`,
          detail: d.edge.predecessorDaysOverdue != null ? days(d.edge.predecessorDaysOverdue) : undefined,
          tone: d.state === 'VIOLATED' ? 'critical' : 'warning',
          target: d.edge.to != null ? { kind: 'deliverable', id: d.edge.to } : undefined,
        })),
        tab: 'dependencies',
      };
    }
    case 'progress': {
      const value = numeric(index, KPI.projectProgress);
      const overduePhases = numeric(index, KPI.overduePhaseCount) ?? 0;
      const phaseProgress = numericByScopeId(data.kpis, KPI.phaseProgress, KpiScope.PHASE);
      const overdue = conspicuousDeliverables(deliverables).filter((d) => d.daysOverdue != null);
      return {
        title: t('report.kpi.projectProgress.label'),
        value,
        status: undefined,
        trend: weekTrend(KPI.projectProgress),
        indicator: { label: t('report.cockpit.overduePhases'), value: `${Math.round(overduePhases)}`, critical: overduePhases > 0 },
        diagnosisTitle: t('report.panel.progressByPhase'),
        diagnosis: [...phaseNames.entries()].slice(0, 6).map(([id, name]) => ({ label: name, value: phaseProgress.get(id) })),
        historyKey: KPI.projectProgress,
        findingsTitle: t('report.panel.overdueItems'),
        findings: overdue.slice(0, 3).map((d) => ({
          key: `lo-${d.id}`,
          text: d.name ?? `#${d.id}`,
          detail: days(d.daysOverdue as number),
          tone: 'critical',
          target: { kind: 'deliverable', id: d.id as number },
        })),
        tab: 'execution',
      };
    }
    case 'actions': {
      const summary = actionSummary(data.actionPoints);
      const today = isoDay(new Date());
      const all = data.actionPoints ?? [];
      const share = (n: number) => (all.length > 0 ? (n / all.length) * 100 : undefined);
      const countOf = (state: string) => all.filter((p) => actionState(p, today) === state).length;
      return {
        title: t('report.cockpit.actionPoints'),
        count: summary.open,
        status: summary.overdue > 0 ? { tone: 'critical', label: t('report.detail.actions.state.overdue') } : undefined,
        indicator: { label: t('report.detail.actions.tiles.dueSoon'), value: `${summary.dueSoon}`, critical: false },
        diagnosisTitle: t('report.detail.actions.byStatus'),
        diagnosis: (['overdue', 'open', 'inProgress', 'done'] as const).map((state) => ({
          label: `${t(`report.detail.actions.state.${state}`)} (${countOf(state)})`,
          value: share(countOf(state)),
          lowerIsBetter: state === 'overdue',
        })),
        findingsTitle: t('report.panel.topActions'),
        findings: byUrgency(all)
          .filter((p) => !['done', 'cancelled'].includes(actionState(p, today)))
          .slice(0, 5)
          .map((p) => ({
            key: `ap-${p.id}`,
            text: actionTitle(t, p, phaseNames),
            detail: p.validationDate ? `${p.validationDate.slice(8, 10)}.${p.validationDate.slice(5, 7)}.` : undefined,
            tone: ACTION_STATE_TONE[actionState(p, today)],
            target: p.id != null ? { kind: 'actionPoint' as const, id: p.id } : undefined,
          })),
        tab: 'actions',
      };
    }
  }
}

export default KpiSidePanel;
